import React, { useImperativeHandle } from 'react';
import { useStore } from '@tanstack/react-form';
import useModal from '@app/component/overlays/modal/ModalRef';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import { CompanyProfileOutput } from '@app/model/company_profile/output/CompanyProfileOutput';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import Authority from '@app/model/authentication/Authority';
import ACLAuthority from '@app/model/profile/ACLAuthority';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CompanyProfileService } from '@app/model/company_profile/service/CompanyProfileService';
import useAppForm from '@app/site/_form';
import Modal from '@app/component/overlays/modal/Modal';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid';
import { RadioField } from '@app/site/_form/field/RadioField';
import Utilities from '@util/Utilities';
import { FieldErrors } from '@app/site/_form/field/FieldErrors';
import {
	useSaveCompanyProfileFormValidation,
} from '@app/site/secured/general-settings/fragment/property-access/modal/SaveCompanyProfileFormValidation';
import { CompanyProfileQueryKeys } from '@app/model/company_profile/service/CompanyProfileQueryKeys';
import { DOMAIN_NAMES } from '@app/model/_common/Domain';
import CompanyHotelLite from '@app/model/company_hotel/CompanyHotelLite';


const resetAccessControlAuthorities = (accessControl: any[]) => accessControl.map((acl) => ({
	...acl,
	domainAuthorities: acl.domainAuthorities.map((domainAuthority: any) => ({
		...domainAuthority,
		authority: ACLAuthority.NONE,
	})),
}));

const ResetAccessControlAuthoritiesOnRoleChange: React.FunctionComponent<{ form: any }> = ({ form }) => {
	const authority = useStore(form.store, (state: any) => state.values.authority);
	const accessControl = useStore(form.store, (state: any) => state.values.accessControl);
	const previousAuthorityRef = React.useRef<Authority | null>(authority);

	React.useEffect(() => {
		if (previousAuthorityRef.current === Authority.ADMIN && authority === Authority.USER) {
			form.setFieldValue('accessControl', resetAccessControlAuthorities(accessControl));
		}

		previousAuthorityRef.current = authority;
	}, [accessControl, authority, form]);

	return null;
};


const SaveCompanyProfileModal: React.FunctionComponent<{
	companyHotels: CompanyHotelLite[],
	companyProfile: CompanyProfileOutput | null,
	onClose?: () => void,
	autoShow?: boolean
	ref?: any
}> = React.forwardRef(
	({
		 companyHotels, companyProfile, onClose = () => {
		}, autoShow,
	 }, ref: React.Ref<any>) => {
		const modal = useModal();

		useImperativeHandle(ref, () => {
			return {
				open: modal.open,
			};
		});

		const toaster = useToaster();
		const [activeIndex, setActiveIndex] = React.useState(0);

		const { validationSchema, defaultValues } = useSaveCompanyProfileFormValidation(companyProfile, companyHotels);

		const queryClient = useQueryClient();
		const saveCompanyProfileMutation = useMutation({
			mutationFn: CompanyProfileService.saveCompanyProfile,
			onSuccess: () => {
				toaster.success('Save User', 'User has been saved successfully!');
				queryClient.invalidateQueries({ queryKey: CompanyProfileQueryKeys.list() });
				onClose();
				if (!!modal.ref) modal.close();
			},
			onError: (error: string) => {
				if (error === 'server.exception.profile.PROFILE_ALREADY_EXISTS')
					form.setErrorMap({
						onSubmit: {
							fields: {
								email: {
									message: 'A user with this email already exists',
									isDirty: true,
								},
							},
						},
					});
			},
		});

		const form = useAppForm({
			defaultValues: defaultValues,
			validators: {
				onChange: validationSchema,
			},
			onSubmit: ({ value }) => {
				saveCompanyProfileMutation.mutateAsync(value).then(() => form.reset()).catch((e) => {
				});
			},
		});

		const findCompanyHotelById = (id: number) => {
			return companyHotels.find((ch) => ch.id === id);
		};

		return <>
			<ResetAccessControlAuthoritiesOnRoleChange form={form} />
			<form.AppForm>
				<form onSubmit={(e) => {
					e.preventDefault();
					e.stopPropagation();
				}}>
					<Modal ref={modal.ref} show={autoShow}
					       title={!!companyProfile ? `Edit ${companyProfile.profile.name}` : 'Add New User'}
					       width="2xlarge" onClose={onClose} actions={
						<form.SubmitButton state="primary">
							Save
						</form.SubmitButton>
					}>

						<div className="mt-6">
							<div className="space-y-4">
								<form.AppField name="name"
								               children={
																 (field) => <field.TextField label="Name" placeholder="Insert name"
									                                           disabled={!!companyProfile} />} />
								<form.AppField name="email"
								               children={
																 (field) => <field.TextField label="Email Address" placeholder="Insert email"
									                                           disabled={!!companyProfile} />} />
								<form.AppField name="departmentId"
								               children={
																 (field) => <field.CompanyDepartmentField label="Department"
									                                                        placeholder="Select department"
									                                                        extraClassName="w-full" emptyOption />} />
								<form.AppField name="authority"
								               children={
																 (field) => <field.SelectField label="Authority"
									                                             extraClassName="w-full"
									                                             options={[
																																 {
																																	 id: 'Admin',
																																	 label: 'Admin',
																																	 value: Authority.ADMIN,
																																 },
																																 { id: 'User', label: 'User', value: Authority.USER },
																															 ]} />}
								/>
							</div>
                            <fieldset className="mt-4">
                                <legend className="text-sm font-medium text-gray-900 mb-2">Pricing rights</legend>
                                <form.AppField name="pricingAuthority">
                                    {() => <div className="flex items-center space-x-3">
                                        <RadioField value={ACLAuthority.NONE} label="None" />
                                        <RadioField value={ACLAuthority.READ} label="Read" />
                                        <RadioField value={ACLAuthority.WRITE} label="Write" />
                                    </div>}
                                </form.AppField>
                            </fieldset>
							<form.Subscribe selector={state => state.values.authority}
							                children={authority => {
																if (authority === Authority.ADMIN) return <div />;
																else {
																	return <form.Field name="accessControl">
																		{(accessControlFields) => <>
																			<form.Field name="accessControl" mode="array">
																				{(ratesAcl) => {
																					return <>
																						<div className="mt-4 mb-4">
																							<FieldErrors meta={ratesAcl.getMeta()} />
																						</div>

																						{ratesAcl.state.value.map((aclField, i) => {
																							const ch = findCompanyHotelById(aclField.companyHotelId)!;
																							return <div className="mt-4" key={i}>
																								<div
																									className="flex items-center justify-between gap-x-2 cursor-pointer"
																	                onClick={() => setActiveIndex(i)}>
																									<div className="flex items-center cursor-pointer">
																										<div className="ml-3 text-sm leading-6 flex">
																											<div className="flex font-medium text-gray-900 mr-2">
																												{ch.mainHotelName}
																											</div>
																											<span
																												className="text-gray-400 font-light">({ch.mainHotelLocation?.city}, {ch.mainHotelLocation?.country?.name})</span>
																										</div>

																									</div>
																									<a className="text-gray-500 hover:text-blue-400">
																										{activeIndex === i &&
																											<ChevronUpIcon className="h-6 w-6" aria-hidden="true" />}
																										{activeIndex !== i &&
																											<ChevronDownIcon className="h-6 w-6" aria-hidden="true" />}
																									</a>
																								</div>

																								<div
																									className={Utilities.classNames('space-y-2 mt-3', activeIndex === i ? 'block' : 'hidden')}>
																									<div className="rounded-md bg-gray-50 px-4 py-3">
																										<form.Field name={`accessControl[${i}].domainAuthorities`}
																		                            mode="array">
																											{(domainAuthorityField) => {
																												return domainAuthorityField.state.value.map((da, daIndex) =>
																													<React.Fragment key={daIndex}>
																														<p
																															className="text-sm font-medium text-gray-900 mb-2 mt-2">• {DOMAIN_NAMES.get(da.domain)}</p>
																														<div
																															className="sm:inline-flex items-center justify-between w-full">
																															<div
																																className="flex items-start sm:items-center mt-3 sm:mt-0">
																																<p
																																	className="text-sm leading-6 text-gray-600 mr-3">Rights:</p>
																																<form.AppField
																																	name={`accessControl[${i}].domainAuthorities[${daIndex}].authority`}>
																																	{(field) => <div
																																		className="flex items-center space-x-3">
																																		<RadioField value={ACLAuthority.NONE}
																										                            label="None" />
																																		<RadioField value={ACLAuthority.READ}
																										                            label="Read" />
																																		<RadioField value={ACLAuthority.WRITE}
																										                            label="Write" />
																																	</div>}
																																</form.AppField>
																															</div>
																														</div>
																													</React.Fragment>);
																											}}
																										</form.Field>

																									</div>
																								</div>
																							</div>;
																						})}
																					</>;
																				}}
																			</form.Field>
																		</>}
																	</form.Field>;
																}
															}} />
						</div>

					</Modal>

				</form>
			</form.AppForm>
		</>;
	});

export default SaveCompanyProfileModal;